import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, FormGroup, Input, Label, Row, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { setLoadingOverlay } from "../../store/slices/loadingOverlaySlice";
import NotificationConfigService from "../../services/notificationConfig";
import Breadcrumbs from "../../components/Breadcrumb";

const ShabbatSettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [breadcrumbItems] = useState([
    { title: t("APPLICATION_TITLE"), link: "#" },
    { title: t("ADMIN.TITLE"), link: "#" },
    { title: t("ADMIN.SHABBAT_SETTINGS"), link: "#" },
  ]);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState({
    startTime: "16:00",
    endTime: "20:00",
    isActive: true,
  });

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    dispatch(setLoadingOverlay(true));
    try {
      const response = await NotificationConfigService.getShabbatSettings();
      if (response.data && response.data.status === 200 && response.data.data) {
        const d = response.data.data;
        setSettings({
          startTime: `${String(d.shabbatStartHour ?? 16).padStart(2, '0')}:${String(d.shabbatStartMinute ?? 0).padStart(2, '0')}`,
          endTime: `${String(d.shabbatEndHour ?? 20).padStart(2, '0')}:${String(d.shabbatEndMinute ?? 0).padStart(2, '0')}`,
          isActive: d.isActive ?? true,
        });
      }
    } catch (error) {
      console.error("Error loading shabbat settings:", error);
      toast.error(t("ADMIN.LOAD_ERROR"));
    } finally {
      setIsLoading(false);
      dispatch(setLoadingOverlay(false));
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    dispatch(setLoadingOverlay(true));
    try {
      const [startH, startM] = (settings.startTime || "16:00").split(":").map(Number);
      const [endH, endM] = (settings.endTime || "20:00").split(":").map(Number);
      const payload = {
        shabbatStartHour: startH,
        shabbatStartMinute: startM,
        shabbatEndHour: endH,
        shabbatEndMinute: endM,
        isActive: settings.isActive,
      };
      const response = await NotificationConfigService.saveShabbatSettings(payload);
      if (response.data && response.data.status === 200) {
        toast.success(t("ADMIN.SAVE_SUCCESS"));
      } else {
        toast.error(response.data?.message || t("ADMIN.SAVE_ERROR"));
      }
    } catch (error) {
      console.error("Error saving shabbat settings:", error);
      toast.error(t("ADMIN.SAVE_ERROR"));
    } finally {
      setIsSaving(false);
      dispatch(setLoadingOverlay(false));
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Breadcrumbs
          title={t("ADMIN.SHABBAT_SETTINGS")}
          breadcrumbItems={breadcrumbItems}
        />

        <Card>
          <CardBody>
            <h4 className="card-title mb-4">{t("ADMIN.SHABBAT_SETTINGS")}</h4>

            {isLoading ? (
              <div className="text-center py-4">
                <Spinner color="primary" />
              </div>
            ) : (
              <>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>{t("ADMIN.SHABBAT_START")}</Label>
                      <div className="text-muted mb-2" style={{ fontSize: "0.85rem" }}>
                        {t("WEEKS.FRI")}
                      </div>
                      <Input
                        type="time"
                        value={settings.startTime}
                        onChange={(e) =>
                          setSettings((prev) => ({ ...prev, startTime: e.target.value }))
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label>{t("ADMIN.SHABBAT_END")}</Label>
                      <div className="text-muted mb-2" style={{ fontSize: "0.85rem" }}>
                        {t("WEEKS.SAT")}
                      </div>
                      <Input
                        type="time"
                        value={settings.endTime}
                        onChange={(e) =>
                          setSettings((prev) => ({ ...prev, endTime: e.target.value }))
                        }
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row className="mt-3">
                  <Col md={6}>
                    <FormGroup check>
                      <Input
                        type="checkbox"
                        id="shabbatActive"
                        checked={settings.isActive}
                        onChange={(e) =>
                          setSettings((prev) => ({ ...prev, isActive: e.target.checked }))
                        }
                      />
                      <Label check htmlFor="shabbatActive">
                        {t("ADMIN.SHABBAT_ACTIVE")}
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>

                <div className="mt-4">
                  <Button
                    color="primary"
                    onClick={handleSave}
                    disabled={isSaving || isLoading}
                  >
                    {isSaving ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        {t("SAVING")}
                      </>
                    ) : (
                      t("SAVE_TITLE")
                    )}
                  </Button>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </div>
    </React.Fragment>
  );
};

export default ShabbatSettings;
